import { computed, ref } from "vue"
import { ProjectApi } from "@/ api/ProjectApi"
import { projectPageStore } from "@/store/project-page"
import { useNotification } from "@/composables/useNotification"
import { ServerExceptionType } from "@/models/etc/types"
import type { AutoJobRefreshAsker, AutoJobTemplateInProject } from "@/models/interfaces/project/autojob-models"
import { getServerException } from "@/utils/pageError"
import {
	applyAuthorsRefresh,
	applyExecutorChange,
	applyExecutorsRefresh,
	buildFilledTemplates,
	createTemplateState,
	hasTemplateErrors,
	validateTemplateState
} from "@/utils/autoJobTemplates"
import type {
	AutoJobTemplateErrors,
	AutoJobTemplatePatch,
	AutoJobTemplateServerError,
	AutoJobTemplateState
} from "@/utils/autoJobTemplates"

import { i18n } from "@/i18n"

const t = (key: string): string => String(i18n.global.t(key))

export type StageAutoJobsPayload = {
	projectId: number
	toStageId: number
	fromStageName: string
	toStageName: string
	templatesFrom: AutoJobTemplateInProject[]
	templatesTo: AutoJobTemplateInProject[]
}

type StageAutoJobsContext = Omit<StageAutoJobsPayload, 'templatesFrom' | 'templatesTo'>

type RefreshKind = 'authors' | 'executors'

const TEMPLATE_SERVER_ERROR: AutoJobTemplateServerError = { field: 'template', messageKey: 'autojob_error_template' }

type UseStageAutoJobsOptions = {
	/** Закрыть окно: после успешной отправки или когда пропал доступ к проекту. */
	onClose: () => void
}

/**
 * Состояние окна авторабот при смене этапа проекта: карточки шаблонов, обновление
 * списков поручителей/исполнителей и отправка. Модалка остаётся тонкой — она только
 * рисует карточки и передаёт сюда их события.
 */
export function useStageAutoJobs({ onClose }: UseStageAutoJobsOptions) {
	const notify = useNotification()

	const templates = ref<AutoJobTemplateState[]>([])
	const context = ref<StageAutoJobsContext | null>(null)
	const isSubmitting = ref(false)

	const fromTemplates = computed(() => templates.value.filter(state => state.direction === 'from'))
	const toTemplates = computed(() => templates.value.filter(state => state.direction === 'to'))

	const validationByKey = computed(() => {
		const today = new Date()
		const result: Record<string, AutoJobTemplateErrors> = {}
		templates.value.forEach(state => {
			result[state.key] = validateTemplateState(state, today)
		})
		return result
	})

	// Ошибка от сервера только показывается: отправить снова можно сразу
	const errorsByKey = computed(() => {
		const result: Record<string, AutoJobTemplateErrors> = {}
		templates.value.forEach(state => {
			const errors = { ...(validationByKey.value[state.key] ?? {}) }
			if (state.serverError && !errors[state.serverError.field]) {
				errors[state.serverError.field] = state.serverError.messageKey
			}
			result[state.key] = errors
		})
		return result
	})

	const isRefreshing = computed(() =>
		templates.value.some(state => state.refreshing.authors || state.refreshing.executors))

	// Неотмеченные шаблоны не отправляются, поэтому их ошибки отправку не блокируют
	const canSubmit = computed(() =>
		!isSubmitting.value
		&& !isRefreshing.value
		&& templates.value.every(state => !state.selected || !hasTemplateErrors(validationByKey.value[state.key] ?? {})))

	const getTemplate = (key: string): AutoJobTemplateState | undefined =>
		templates.value.find(state => state.key === key)

	// Шаблоны меняются целиком: так карточка получает новый объект в пропсе.
	// Если шаблона уже нет (окно закрыли, шаблон удалён), правка просто пропадает
	const replaceTemplate = (key: string, update: (state: AutoJobTemplateState) => AutoJobTemplateState) => {
		const index = templates.value.findIndex(state => state.key === key)
		if (index === -1) {
			return
		}
		templates.value.splice(index, 1, update(templates.value[index]))
	}

	// Id шаблона из ответа сервера ищем в том же порядке, в каком шаблоны ушли в запросе
	const findKeyByTemplateId = (id: number | undefined): string | null => {
		if (id == null) {
			return null
		}
		const found = toTemplates.value.find(state => state.source.Id === id)
			?? fromTemplates.value.find(state => state.source.Id === id)
		return found?.key ?? null
	}

	const init = (payload: StageAutoJobsPayload) => {
		context.value = {
			projectId: payload.projectId,
			toStageId: payload.toStageId,
			fromStageName: payload.fromStageName,
			toStageName: payload.toStageName
		}
		templates.value = [
			...(payload.templatesFrom ?? []).map(source => createTemplateState(source, 'from')),
			...(payload.templatesTo ?? []).map(source => createTemplateState(source, 'to'))
		]
	}

	const reset = () => {
		templates.value = []
		context.value = null
	}

	const updateTemplate = (key: string, patch: AutoJobTemplatePatch) => {
		replaceTemplate(key, state => {
			const next: AutoJobTemplateState = { ...state, ...patch, serverError: null }
			if (patch.executorKeys) {
				next.executorKeys = applyExecutorChange(state.executorKeys, patch.executorKeys, state.source.ExectutorHolders)
			}
			return next
		})
	}

	const setRefreshing = (key: string, kind: RefreshKind, value: boolean) => {
		replaceTemplate(key, state => ({ ...state, refreshing: { ...state.refreshing, [kind]: value } }))
	}

	// Ошибка уровня проекта (например, пропал доступ): при перечитывании страница сама покажет заглушку
	const closeWithProjectReload = async (projectId: number) => {
		await projectPageStore().reloadAfterStageChange(projectId)
		onClose()
	}

	const runRefresh = async <T>(
		key: string,
		kind: RefreshKind,
		load: (request: AutoJobRefreshAsker) => Promise<T>,
		apply: (state: AutoJobTemplateState, response: T) => AutoJobTemplateState
	) => {
		const state = getTemplate(key)
		const ctx = context.value
		if (!state || !ctx || state.refreshing[kind]) {
			return
		}

		setRefreshing(key, kind, true)
		try {
			const response = await load({ Id: ctx.projectId, StageId: ctx.toStageId, TemplateId: state.source.Id })
			// Пока шёл запрос, окно закрыли или открыли заново
			if (context.value !== ctx) {
				return
			}
			replaceTemplate(key, current => apply(current, response))
		} catch (error) {
			console.error(error)
			if (context.value !== ctx) {
				return
			}

			const exception = getServerException(error)
			if (exception?.Type === ServerExceptionType.Project) {
				await closeWithProjectReload(ctx.projectId)
				return
			}
			// needRefresh не сбрасываем: следующее открытие списка повторит запрос
			if (exception) {
				notify.warning(t('autojob_title_short'), exception.Message || t('unknown_error'))
			}
		} finally {
			setRefreshing(key, kind, false)
		}
	}

	const refreshAuthors = (key: string) =>
		runRefresh(key, 'authors', ProjectApi.refreshAutoJobAuthors, applyAuthorsRefresh)

	const refreshExecutors = (key: string) =>
		runRefresh(key, 'executors', ProjectApi.refreshAutoJobExecutors, applyExecutorsRefresh)

	// Подтипов ошибок авторабот пока нет, поэтому разбор общий: Id шаблона берём из OptionalParams
	const handleSubmitError = async (error: unknown, ctx: StageAutoJobsContext) => {
		const exception = getServerException(error)

		const key = findKeyByTemplateId(exception?.OptionalParams?.[0])
		if (key) {
			replaceTemplate(key, state => ({
				...state,
				serverError: TEMPLATE_SERVER_ERROR,
				needRefresh: { authors: true, executors: true }
			}))
			refreshAuthors(key)
			refreshExecutors(key)
			notify.warning(t('autojob_title_short'), exception?.Message || t('autojob_error_template'))
			return
		}

		if (exception?.Type === ServerExceptionType.Project) {
			notify.danger(t('autojob_title_short'), exception.Message || t('unknown_error'))
			await closeWithProjectReload(ctx.projectId)
			return
		}

		notify.danger(t('autojob_title_short'), exception?.Message || t('unknown_error'))
	}

	const submit = async () => {
		const ctx = context.value
		if (!ctx || !canSubmit.value) {
			return
		}

		isSubmitting.value = true
		try {
			await projectPageStore().changeProjectStageWithAutoJobs({
				Id: ctx.projectId,
				StageId: ctx.toStageId,
				Templates: buildFilledTemplates(templates.value)
			})
			// Если окно уже закрыли, закрывать нечего: стор проект всё равно перечитал
			if (context.value === ctx) {
				onClose()
			}
		} catch (error) {
			console.error(error)
			if (context.value === ctx) {
				await handleSubmitError(error, ctx)
			}
		} finally {
			isSubmitting.value = false
		}
	}

	return {
		context,
		fromTemplates,
		toTemplates,
		errorsByKey,
		isSubmitting,
		canSubmit,
		init,
		reset,
		updateTemplate,
		refreshAuthors,
		refreshExecutors,
		submit,
	}
}
